import { Controller } from "@hotwired/stimulus";
import ApexCharts from "apexcharts";

const markerSize = 4;

export default class extends Controller {
  static values = {
    snapshots: Array,
    animated: { type: Boolean, default: false },
  };

  static targets = ["chart"];

  connect() {
    requestAnimationFrame(() => this.render());
    console.log(this.snapshotsValue);
  }

  disconnect() {
    this._chart?.destroy();
  }

  render() {
    this._chart?.destroy();

    const snapshots = this.snapshotsValue;

    if (snapshots.length === 0) {
      this.renderEmpty();
      return;
    }

    // Build cumulative reach series
    let cumulative = 0;
    const series = snapshots.map((s) => {
      cumulative = s.cumulative_reach;
      return {
        x: new Date(s.created_at).getTime(),
        y: cumulative,
        raw: s,
        cumulative,
      };
    });

    if (series.length === 1) {
      series.unshift({
        x: series[0].x - 1,
        y: 0,
        raw: series[0].raw,
        cumulative: 0,
      });
    }

    this._chart = new ApexCharts(this.chartTarget, {
      ...this.baseChartConfig({ cssClass: "reach-chart" }),
      series: [{ data: series.map((p) => ({ x: p.x, y: p.y })) }],
      stroke: { curve: "straight", width: 1.5, colors: ["#3b82f6"] },
      fill: {
        type: "solid",
        opacity: 0.08,
      },
      markers: {
        size: 0,
        hover: { size: markerSize, sizeOffset: 0 },
        colors: ["#3b82f6"],
        strokeColors: "#3b82f6",
        strokeWidth: 0,
      },
      xaxis: {
        type: "datetime",
        tooltip: { enabled: false },
        crosshairs: { show: false },
      },
      yaxis: { crosshairs: { show: false } },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const s = series[dataPointIndex];
          if (!s) return "";
          const d = s.raw;
          const date = new Date(d.created_at);
          const dateStr = date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          const extraRows =
            d.rank != null
              ? `
                <span>Rank: <b>#${d.rank ?? "—"}</b></span>
                <span>Views: <b>${this.fmt(d.video_views)}</b></span>
                <span>Likes: <b>${d.like_count}</b></span>`
              : "";
          return `
            <div class="bg-base-100 border border-base-300 rounded-md px-2.5 py-2 text-base-content/80 min-w-27.5 text-xs">
              <div class="font-medium">${dateStr}</div>
              <div class="flex flex-col gap-0.5 mt-1 text-[10px]">${extraRows}
                <span>Reach: <b>+${this.fmt(d.reach)}</b></span>
                <span>Total: <b>${this.fmt(s.cumulative)}</b></span>
              </div>
            </div>
          `;
        },
      },
    });

    this._chart.render();
  }

  renderEmpty() {
    const N = 10;
    const data = Array.from({ length: N }, (_, i) => ({
      x: i,
      y: Math.max(
        0.05,
        i / (N - 1) +
          (Math.random() - 0.5) * 0.25 * Math.sin(Math.PI * (i / (N - 1))),
      ),
    }));

    this._chart = new ApexCharts(this.chartTarget, {
      ...this.baseChartConfig(),
      series: [{ data }],
      stroke: {
        curve: "straight",
        width: 1.5,
        colors: ["#9ca3af"],
        dashArray: 4,
      },
      fill: {
        type: "solid",
        colors: ["#9ca3af"],
        opacity: 0.08,
      },
      markers: { size: 0, hover: { size: 0 } },
      xaxis: { type: "numeric" },
      tooltip: {
        custom: () => `
          <div style="width:220px" class="bg-base-100 border border-base-300 rounded-md px-2.5 py-2 text-base-content/60 text-[10px] whitespace-normal">
            We'll start collecting cumulative reach data once the first comment is submitted to YouTube
          </div>
        `,
      },
    });

    this._chart.render();
  }

  baseChartConfig({ cssClass } = {}) {
    return {
      chart: {
        type: "area",
        height: 56,
        width: "100%",
        sparkline: { enabled: true },
        cssClass,
        animations: {
          enabled: true,
          easing: "linear",
          speed: 1000,
          animateGradually: { enabled: false },
        },
        // animations: {
        //   enabled: false,
        // },
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "transparent",
        events: {
          mounted: (chartCtx) => this.removeTitles(chartCtx),
        },
      },
      dataLabels: { enabled: false },
      grid: {
        padding: {
          left: markerSize,
          right: markerSize,
          top: markerSize,
          bottom: markerSize,
        },
      },
    };
  }

  fmt(n) {
    if (n == null) return "—";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return n.toLocaleString();
  }

  removeTitles(chartCtx) {
    const titles = chartCtx.el.querySelectorAll("svg title");
    titles.forEach((t) => t.remove());
  }
}
